import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Download, Check, AlertTriangle } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Label } from '@/shared/components/ui/label';
import { formatDateTime } from '@/shared/utils/date';

interface MfaRecoveryCodesViewProps {
  codes: string[];
  onConfirmSaved: () => void;
  confirmLabel?: string;
}

export const MfaRecoveryCodesView: React.FC<MfaRecoveryCodesViewProps> = ({
  codes,
  onConfirmSaved,
  confirmLabel,
}) => {
  const { t } = useTranslation('mfa');
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);

  const resolvedConfirmLabel = confirmLabel ?? t('recoveryCodesView.defaultConfirmLabel');

  const text = codes.join('\n');

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([
      t('recoveryCodesView.downloadHeader'),
      t('recoveryCodesView.downloadGeneratedAt', { date: formatDateTime(new Date().toISOString()) }),
      t('recoveryCodesView.downloadUsageNotice'),
      t('recoveryCodesView.downloadKeepSafe'),
      ...codes.map(c => c + '\n'),
    ], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'invexis-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          {t('recoveryCodesView.warningPrefix')}<strong>{t('recoveryCodesView.warningEmphasis')}</strong>
          {' '}{t('recoveryCodesView.warningSuffix')}
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg font-mono text-sm">
        {codes.map((code, i) => (
          <div key={i} className="px-3 py-2 bg-background rounded border text-center">
            {code}
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={handleCopy} type="button">
          {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
          {copied ? t('recoveryCodesView.copiedLabel') : t('recoveryCodesView.copyLabel')}
        </Button>
        <Button variant="outline" className="flex-1" onClick={handleDownload} type="button">
          <Download className="w-4 h-4 mr-2" />
          {t('recoveryCodesView.downloadButton')}
        </Button>
      </div>

      <div className="flex items-start space-x-2 pt-2">
        <Checkbox
          id="recovery-saved"
          checked={acknowledged}
          onCheckedChange={(c) => setAcknowledged(!!c)}
        />
        <Label htmlFor="recovery-saved" className="text-sm leading-tight cursor-pointer">
          {t('recoveryCodesView.acknowledgeLabel')}
        </Label>
      </div>

      <Button
        className="w-full"
        disabled={!acknowledged}
        onClick={onConfirmSaved}
        type="button"
      >
        {resolvedConfirmLabel}
      </Button>
    </div>
  );
};
